import { AlchemyStar, ChainLink, DragIndicator } from '@creditornot/cb-icons';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  ImageContainer,
  ImageFrame,
  ImageName,
  ImageOverlayItemsContainer,
  ImageOverlayItemsGroup,
  SelectedCheckbox,
} from './ImageCardStyles';
import { InfoIcon } from './InfoIcon';
import { LazyImage } from './LazyImage';

type Props = {
  src: string;
  name: string;
  blurhash?: string;
  enhanced?: boolean;
  attachedToOffer?: boolean;
  selected?: boolean;
  selectable?: boolean;
  draggable?: boolean;
  imageFrame?: React.ReactNode;
};

const StyledDragIndicator = styled(DragIndicator)`
  width: 20px;
  height: 20px;
  background-color: ${color.bgFillTransparent};
  border-radius: 4px;
`;

export const ImageCardContent = ({
  src,
  name,
  blurhash,
  enhanced,
  attachedToOffer,
  draggable,
  selectable,
  selected,
  imageFrame,
}: Props) => {
  return (
    <>
      {imageFrame || (
        <ImageFrame>
          <LazyImage src={src} alt={name} blurhash={blurhash} />
        </ImageFrame>
      )}
      <ImageName title={name}>{name}</ImageName>
      <ImageOverlayItemsContainer>
        <ImageOverlayItemsGroup>
          {draggable && <StyledDragIndicator />}
          {selectable && <SelectedCheckbox checked={selected} readOnly />}
        </ImageOverlayItemsGroup>
        <ImageOverlayItemsGroup>
          {attachedToOffer && (
            <InfoIcon info={<FormattedMessage id="photo-shoot.photo-linked" />} icon={<ChainLink />} />
          )}
          {enhanced && (
            <InfoIcon
              info={<FormattedMessage id="shared-menu.editor.image-gallery.enhanced-with-ai" />}
              icon={<AlchemyStar />}
            />
          )}
        </ImageOverlayItemsGroup>
      </ImageOverlayItemsContainer>
    </>
  );
};

type ImageCardProps = Props & {
  onClick?: () => void;
};

export const ImageCard = ({
  src,
  name,
  blurhash,
  enhanced,
  attachedToOffer,
  draggable,
  selectable,
  selected,
  onClick,
}: ImageCardProps) => {
  return (
    <ImageContainer onClick={onClick} $draggable={draggable} $selected={selected} $clickable={Boolean(onClick)}>
      <ImageCardContent
        src={src}
        name={name}
        blurhash={blurhash}
        enhanced={enhanced}
        attachedToOffer={attachedToOffer}
        draggable={draggable}
        selectable={selectable}
        selected={selected}
      />
    </ImageContainer>
  );
};
